import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';

const BillingsPage = () => {
  const [courierId, setCourierId] = useState('');
  const [couriers, setCouriers] = useState([]); // Ensure this is always an array
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [rates, setRates] = useState({
    range1: 0,
    range2: 0,
    range3: 0,
    range4: 0,
    range5: 0,
    range6: 0,
  });
  const [costs, setCosts] = useState({
    bagAndSealsCost: 0,
    AWDCost: 0,
    DeliveryCost: 0,
    BagCost: 0,
    StandardCost: 0,
    EContainerCost: 0,
    DContainerCost: 0,
    EHContainerCost: 0,
  });
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    fetchCourier();
  }, []);

  // Fetch the courier list (assuming it's needed for a select field)
  const fetchCourier = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/courier/all`);
      if (!response.ok) throw new Error('Failed to fetch courier methods');
      const data = await response.json();
      
      // Ensure the fetched data is an array
      if (Array.isArray(data)) {
        setCouriers(data);
        setCourierId(data[0]?.id); // Set default courierId if available
      } else {
        throw new Error('Courier data is not in the expected format');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Call the API to generate the invoice
  const generateInvoice = async () => {
    if (!courierId || !startDate || !endDate || !rates) {
      setError('Please fill in all fields.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/generateInvoice/${courierId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rates,
          costs,
          startDate,
          endDate,
        }),
      });

      if (!response.ok) throw new Error('Failed to generate invoice');

      const data = await response.json();
      setInvoice(data); // Store the invoice data returned from the backend
    } catch (err) {
      setError(err.message || 'Failed to generate invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="w-[90%] px-10 p-6 bg-white shadow-md rounded-lg">
        <h1 className="text-2xl font-bold text-center mb-6">Generate Invoice</h1>

        {/* Date Range Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        {/* Courier Section */}
        <div className="mb-6">
          <label htmlFor="courier-select" className="block text-sm font-medium text-gray-700">Select Courier</label>
          <select
            id="courier-select"
            value={courierId}
            onChange={(e) => setCourierId(e.target.value)}
            className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            {couriers && couriers.length > 0 ? (
              couriers.map((courier) => (
                <option key={courier.id} value={courier.id}>
                  {courier.username} ({courier.code})
                </option>
              ))
            ) : (
              <option disabled>No couriers available</option>
            )}
          </select>
        </div>

        {/* Costs Section: Above Rates */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Additional Costs</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Bags & Seals Cost</label>
              <input
                type="number"
                value={costs.bagAndSealsCost}
                onChange={(e) => setCosts({ ...costs, bagAndSealsCost: e.target.value })}
                className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">AWD Cost</label>
              <input
                type="number"
                value={costs.AWDCost}
                onChange={(e) => setCosts({ ...costs, AWDCost: e.target.value })}
                className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Delivery Cost</label>
              <input
                type="number"
                value={costs.DeliveryCost}
                onChange={(e) => setCosts({ ...costs, DeliveryCost: e.target.value })}
                className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Rates Section */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Set Rates</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {['range1', 'range2', 'range3', 'range4', 'range5', 'range6'].map((range, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-gray-700">{range === 'range1' ? '0-10 lb' :
                      range === 'range2' ? '11-20 lb' :
                      range === 'range3' ? '21-50 lb' :
                      range === 'range4' ? '51-100 lb' :
                      range === 'range5' ? '101-150 lb' :
                      '150+ lb'}</label>
                <input
                  type="number"
                  value={rates[range]}
                  onChange={(e) => setRates({ ...rates, [range]: e.target.value })}
                  className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Additional Costs Section: Below Rates */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-4">Other Costs</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Bag Cost</label>
              <input
                type="number"
                value={costs.BagCost}
                onChange={(e) => setCosts({ ...costs, BagCost: e.target.value })}
                className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Standard Cost</label>
              <input
                type="number"
                value={costs.StandardCost}
                onChange={(e) => setCosts({ ...costs, StandardCost: e.target.value })}
                className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">E Container Cost</label>
              <input
                type="number"
                value={costs.EContainerCost}
                onChange={(e) => setCosts({ ...costs, EContainerCost: e.target.value })}
                className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">D Container Cost</label>
              <input
                type="number"
                value={costs.DContainerCost}
                onChange={(e) => setCosts({ ...costs, DContainerCost: e.target.value })}
                className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">EH Container Cost</label>
              <input
                type="number"
                value={costs.EHContainerCost}
                onChange={(e) => setCosts({ ...costs, EHContainerCost: e.target.value })}
                className="mt-2 block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center">
          <button
            onClick={generateInvoice}
            disabled={loading}
            className="w-1/2 py-3 px-6 bg-blue-800 text-white rounded-md shadow-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:ring-opacity-50"
          >
            {loading ? 'Generating Invoice...' : 'Generate Invoice'}
          </button>
        </div>

        {/* Error Message */}
        {error && <div className="mt-4 text-red-500 text-center">{error}</div>}

        {/* Invoice Breakdown */}
        {invoice && (
  <div className="mt-6 p-4 border-t border-gray-300">
    <h2 className="text-xl font-semibold mb-4">Invoice Breakdown</h2>
    
    <div className="mb-6">
      <p className="text-lg font-medium">
        <strong>Total Packages:</strong> {invoice.totalPackages}
      </p>
    </div>

    <h3 className="text-lg font-semibold mb-2">Cost Breakdown</h3>

    <div className="overflow-x-auto">
      <table className="min-w-full table-auto border-collapse">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-left border-b">Description</th>
            <th className="px-4 py-2 text-left border-b">Amount</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(invoice.breakdown).map(([range, cost]) => (
            <tr key={range} className="hover:bg-gray-50">
              <td className="px-4 py-2 border-b">{range}</td>
              <td className="px-4 py-2 border-b text-right">${cost.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className='flex justify-between'>
      <p className="text-lg font-medium p-4">
        <strong>Total Cost:</strong> 
      </p>
      <p className='text-lg font-medium p-4'>${invoice.totalCost.toFixed(2)}</p>
      </div>
    </div>
  </div>
)}

      </div>
    </Layout>
  );
};

export default BillingsPage;
